import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import FirmAgreements from '../../components/doorlypro/FirmAgreements';
import BrokerNomination from '../../components/doorlypro/BrokerNomination';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🚨 W-9 IS GONE FROM WHAT A PARTNER CAN UPLOAD. Doorly pays the FIRM, so the
// W-9 identifies the entity receiving the money — it moved to partner_accounts
// and its task now belongs to the account-level holder. Leaving it here invited
// an agent to upload their personal W-9 against a fee that will never be paid to
// them, and nothing downstream reads a partner-level one any more.
// 🚨 'license' IS GONE FROM WHAT A PARTNER MAY UPLOAD. Jesse: licence evidence
// comes from the registry, not an upload. Doorly checks a realtor's number
// against the TREC register and reads an originator's NMLS record on Consumer
// Access; a photograph of a card proves strictly less than either — it is
// self-supplied, cannot be validated automatically, and says nothing about
// whether the licence is still active today.
//
// ⚠️ OFFERING IT WOULD BE WORSE THAN USELESS NOW. Nothing reads a newly uploaded
// licence: the dashboard task is gone and the payout rung it fed is gone, so a
// partner who uploaded one would have done work that changes nothing and would
// reasonably expect it to have.
const DOC_TYPES = [
  { value: 'agreement', label: 'Agreement' },
  { value: 'other', label: 'Other' },
];

// 🔑 …EXCEPT FOR THE ONE PERSON IT IS ACTUALLY FOR. The W-9 identifies the entity
// Doorly PAYS, which is the firm, and an account-level holder is the only member
// who can supply the firm's. It came off this list when it moved to
// partner_accounts and the only place left to upload it was Firm Settings — a
// page that otherwise duplicated the banking editor on the profile, and is now
// gone. So it returns here, for the holder alone.
//
// ⚠️ STILL NOT OFFERED TO AN ORDINARY MEMBER. The reason it was removed has not
// changed: an agent uploading their personal W-9 against a fee that will never be
// paid to them is a document nothing downstream reads.
const docTypesFor = (isAccountHolder) => (isAccountHolder
  ? [...DOC_TYPES, { value: 'w9', label: 'W-9 (firm)' }]
  : DOC_TYPES);

// ⚠️ THE LABEL MAP KEEPS W-9, and the two lists are deliberately not the same.
// DOC_TYPES is what may be uploaded NOW; this names what is already on file, and
// partners who uploaded one before the move still have it. Dropping the label
// would render their existing document as a blank type.
// ⚠️ AND THE LABEL MAP KEEPS BOTH RETIRED TYPES. DOC_TYPES is what may be
// uploaded NOW; this names what is already on file. Five partners have a licence
// document and partners who uploaded a W-9 before it moved to the account still
// have theirs — dropping either label would render their document as a blank
// type, which reads as data loss.
const TYPE_LABELS = { ...Object.fromEntries(DOC_TYPES.map((t) => [t.value, t.label])),
  w9: 'W-9', license: 'License (no longer requested)',
  mlo_license: 'MLO license (no longer requested)',
  real_estate_license: 'Real estate license (no longer requested)' };

export default function DoorlyProDocuments() {
  const { token } = useDoorlyProAuth();
  // 🔑 READ FROM /me, NOT FROM THE TOKEN. account_role is granted after
  // registration — when a firm is created or an invite confers it — so a JWT
  // minted at sign-up would carry a stale answer for the life of the session.
  const [isAccountHolder, setIsAccountHolder] = useState(false);
  // 🚨 BROKER ACCOUNTS ONLY, and read from /me for the same reason account_role
  // is: a JWT minted at sign-up carries whatever was true then.
  const [partnerType, setPartnerType] = useState(null);
  const [authTemplateError, setAuthTemplateError] = useState('');
  const [authTemplateBusy, setAuthTemplateBusy] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  // 🔑 THE DEFAULT MOVES WITH THE LIST. It was 'license', which is no longer an
  // option — a select whose value is not among its options renders blank.
  const [docType, setDocType] = useState('agreement');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [viewingAgreement, setViewingAgreement] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // 🔑 AN UPLOADED FILE OPENS BY ID, WITH THE PARTNER'S TOKEN (2026-10-29): GET /api/doorlypro/documents/:id/file
  // serves it from the database. The old link was a server disk path, which nothing served.
  const openStoredFile = async (doc) => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API_URL}${doc.file_url}`, { headers: authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      if (win) win.location.href = url; else window.location.href = url;
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      let message = 'We could not open that document. Please try again.';
      try {
        const text = err.response?.data && typeof err.response.data.text === 'function' ? await err.response.data.text() : null;
        const parsed = text ? JSON.parse(text) : null;
        if (parsed?.error) message = parsed.error;
      } catch (_) { /* the default sentence stands */ }
      setError(message);
    }
  };

  const fetchDocuments = () => {
    axios
      .get(`${API_URL}/api/doorlypro/documents`, { headers: authHeaders })
      .then((res) => setDocuments(res.data?.documents || res.data || []))
      .catch(() => setError('Failed to load documents.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) return;
    fetchDocuments();
    axios.get(`${API_URL}/api/doorlypro/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setIsAccountHolder(r.data?.partner?.account_role === 'broker');
        setPartnerType(r.data?.partner?.partner_type || null);
      })
      // ⚠️ FALSE ON FAILURE, WHICH HIDES THE OPTION RATHER THAN OFFERING ONE THE
      // SERVER WOULD REFUSE. requireBroker gates the upload either way.
      .catch(() => setIsAccountHolder(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 🔑 GENERATED ON REQUEST, NEVER A STORED FILE ──────────────────────────
  //
  // The server renders it from constants/brokerBorrowerAuthorization.js every
  // time. A stored PDF stops tracking the template, and the first time the
  // wording changes the printable form and the signing page disagree about what
  // the borrower is agreeing to.
  //
  // ⚠️ AND THE 409 IS SHOWN, NOT SWALLOWED. The refusal is the useful answer:
  // no broker on record has an NMLS number captured yet, and a form reading
  // "(NMLS #)" asserts a registration nobody holds. A generic "download failed"
  // would send a broker to support for something a sentence can explain.
  const downloadAuthorizationTemplate = async () => {
    setAuthTemplateBusy(true);
    setAuthTemplateError('');
    try {
      const res = await axios.get(`${API_URL}/api/broker-authorization/template.pdf`, {
        headers: authHeaders, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Borrower-Authorization.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // ⚠️ THE BODY ARRIVES AS A BLOB because responseType said so, even on the
      // error. Reading it as text is what makes the server's sentence visible.
      let message = 'We could not produce the form just now. Please try again.';
      try {
        const body = err.response?.data;
        const text = body && typeof body.text === 'function' ? await body.text() : null;
        const parsed = text ? JSON.parse(text) : null;
        if (parsed?.error) message = parsed.error;
      } catch (_) { /* leave the fallback */ }
      setAuthTemplateError(message);
    } finally {
      setAuthTemplateBusy(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
    setUploadError('');
    setUploadSuccess(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) { setUploadError('Please select a file.'); return; }
    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('doc_type', docType);
    // 🚨 THE W-9 IS THE FIRM'S DOCUMENT AND HAS ITS OWN ENDPOINT. It is stored on
    // partner_accounts, behind requireBroker, with a narrower accepted-type list —
    // posting it to the partner document list would file the firm's tax record
    // against one person and leave the account's own column empty, which is the
    // split the move to partner_accounts existed to end.
    const isFirmW9 = docType === 'w9';
    try {
      await axios.post(`${API_URL}/api/doorlypro/${isFirmW9 ? 'firm/w9' : 'documents'}`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setUploadSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLoading(true);
      fetchDocuments();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 700 }}>
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Documents</h1>
            <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>Upload and manage your partner documents.</p>
          </div>

          {/* ⚠️ RENDERS NOTHING FOR A NON-BROKER, AND DECIDES THAT FROM THE
              SERVER. The endpoint 404s for anyone without the role, which the
              component treats as "no section" rather than an error — so an
              ordinary agent sees the page exactly as before and is never told an
              enterprise view exists. Same hidden-not-disabled rule as the nav. */}
          {/* ⚠️ ON THE DOCUMENTS PAGE, WHERE THE PROBLEM IS VISIBLE. This is where
              an agent sees their agreement, so it is where "waiting on a broker
              who is not here" belongs — a prompt on the dashboard would be one
              screen away from the thing it is about. */}
          <BrokerNomination token={token} />
          <FirmAgreements token={token} />

          {/* ── 🚨 THE BORROWER AUTHORIZATION, FOR BROKER ACCOUNTS ONLY ───────
              A realtor partner has no borrower to authorize and no credit report
              to share; offering them this would invite them to collect a credit
              authorization Doorly never asked for. The server refuses either way
              — this hides an option it would refuse rather than relying on it. */}
          {partnerType === 'loan_officer' && (
            <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, marginBottom: 24 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
                Borrower Authorization
              </h2>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.6 }}>
                One form your borrower signs: it lets you share their application and credit
                report with Doorly, lets Doorly obtain, reissue or use their credit report, and
                lets Doorly verify employment, income and assets. Valid for 180 days.
                {/* 🔑 THE DEFAULT IS NAMED HERE so the printable form reads as the
                    fallback it is, rather than as the normal route. */}
                {' '}Your company name and NMLS number are filled in; the borrower's details are
                left blank. When you submit a file, you can instead invite each borrower to sign
                it electronically — that is the quicker route and Doorly keeps the record.
              </p>
              {authTemplateError && (
                <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13, lineHeight: 1.5 }}>
                  {authTemplateError}
                </div>
              )}
              <button
                type="button"
                onClick={downloadAuthorizationTemplate}
                disabled={authTemplateBusy}
                style={{
                  padding: '9px 20px', borderRadius: 8, backgroundColor: '#3a6ad9',
                  color: '#fff', border: 'none', fontWeight: 600, fontSize: 13,
                  cursor: authTemplateBusy ? 'not-allowed' : 'pointer',
                  opacity: authTemplateBusy ? 0.5 : 1,
                }}
              >
                {authTemplateBusy ? 'Preparing…' : 'Download printable form (PDF)'}
              </button>
            </div>
          )}

          {/* Upload form */}
          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 14px' }}>Upload Document</h2>

            {uploadSuccess && (
              <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, backgroundColor: '#f0fde4', border: '1px solid #d4f9a0', color: '#acf44e', fontSize: 13 }}>
                Document uploaded successfully.
              </div>
            )}
            {uploadError && (
              <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13 }}>
                {uploadError}
              </div>
            )}

            <form onSubmit={handleUpload}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                    Document Type
                  </label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    style={{
                      width: '100%', height: 44, padding: '0 12px', borderRadius: 8,
                      border: '1px solid #e5e7eb', fontSize: 13, color: '#111827',
                      backgroundColor: '#fff', outline: 'none', cursor: 'pointer',
                    }}
                  >
                    {docTypesFor(isAccountHolder).map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                    File
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    style={{
                      width: '100%', height: 44, padding: '10px 12px', borderRadius: 8,
                      border: '1px solid #e5e7eb', fontSize: 12, color: '#374151',
                      backgroundColor: '#fff', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={uploading || !selectedFile}
                style={{
                  padding: '9px 20px', borderRadius: 8, backgroundColor: '#3a6ad9',
                  color: '#fff', border: 'none', fontWeight: 600, fontSize: 13,
                  cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer',
                  opacity: uploading || !selectedFile ? 0.5 : 1,
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </form>
          </div>

          {/* Document list */}
          {error && (
            <div style={{ padding: '12px 16px', borderRadius: 8, backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6' }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Uploaded Documents</h2>
            </div>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading...</div>
            ) : documents.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                No documents uploaded yet.
              </div>
            ) : (
              documents.map((doc, idx) => (
                <div key={doc.id || idx} style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px',
                  borderBottom: idx < documents.length - 1 ? '1px solid #f3f4f6' : 'none',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, backgroundColor: '#eff3fe',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="16" height="16" fill="none" stroke="#3a6ad9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.filename || doc.original_name || 'Document'}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                      {TYPE_LABELS[doc.doc_type] || doc.doc_type} &bull;{' '}
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                  {doc.is_agreement ? (
                    <button onClick={() => setViewingAgreement(doc)}
                      style={{ fontSize: 12, color: '#3a6ad9', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
                      View Agreement
                    </button>
                  ) : doc.file_url ? (
                    <button onClick={() => openStoredFile(doc)}
                      style={{ fontSize: 12, color: '#3a6ad9', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
                      View
                    </button>
                  ) : (doc.url || doc.pdf_url) ? (
                    <a href={doc.pdf_url || doc.url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: '#3a6ad9', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
                      View
                    </a>
                  ) : doc.available === false ? (
                    <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>No longer available — please upload it again</span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
        {/* Agreement Viewer Modal */}
        {viewingAgreement && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Partner Agreement</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {viewingAgreement.pdf_url && (
                    <a href={viewingAgreement.pdf_url} download="DoorlyPro-Agreement.pdf"
                      style={{ padding: '7px 14px', background: '#3a6ad9', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>Download PDF</a>
                  )}
                  <button onClick={() => setViewingAgreement(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1 }}>x</button>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', background: '#f9fafb' }}>
                <div style={{ background: '#fff', maxWidth: 560, margin: '0 auto', padding: 40, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', borderRadius: 4, fontFamily: 'Georgia, serif' }}>
                  <div style={{ textAlign: 'center', marginBottom: 28, borderBottom: '2px solid #111827', paddingBottom: 16 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{(viewingAgreement.filename || 'Partner Agreement').toUpperCase()}</div>
                    <div style={{ fontSize: 10, color: '#6b7280' }}>Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity</div>
                  </div>
                  <div style={{ marginBottom: 20, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Partner Information</div>
                    {[['Name', viewingAgreement.signed_name], ['Signed', viewingAgreement.uploaded_at ? new Date(viewingAgreement.uploaded_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '']].map(([l, v]) => (
                      <div key={l} style={{ display: 'flex', gap: 10, marginBottom: 4, fontSize: 12 }}>
                        <span style={{ fontWeight: 700, color: '#374151', width: 60 }}>{l}:</span>
                        <span style={{ color: '#111827' }}>{v || ''}</span>
                      </div>
                    ))}
                  </div>
                  {/* 🚨 THE THIRTEEN-CLAUSE SUMMARY THAT WAS HERE IS GONE, AND IT
                      WAS FALSE. It was a hardcoded prose summary of the v1.0
                      Partner Agreement, rendered to every partner regardless of
                      what they actually signed — and it stated terms on a
                      partner-facing surface that the manual forbids staff from
                      stating at all:

                        §4  "a partner fee equal to 2.00% of the home contract
                            purchase price" and "Payment within 30 calendar days"
                            — a fee AND a payment timing.
                        §5  "One (1) year term with auto-renewal", "30 days notice".
                        §2  the v1.0 introduce-and-refer duties, with no Home
                            Selection (v2.1 §1.5) and no §2.5 escalation.

                      🔑 AND A SUMMARY OF A VERSIONED INSTRUMENT IS A FOURTH COPY
                      THAT DRIFTS. Realtors are on v2.2 and brokers on wholesale
                      v2.1, held in constants/agreements/*.js; this froze at v1.0
                      and nothing made it move. Rendering the CURRENT clause arrays
                      instead would be no better — a partner still on v1.0.0 would
                      be shown terms they never signed, which is worse than a stale
                      summary because it looks authoritative.

                      ⚠️ SO IT POINTS AT THEIR OWN EXECUTED DOCUMENT, the rule the
                      training decks already follow. The facts below are theirs —
                      their name, their date, their file — and the terms live in
                      the instrument they signed. */}
                  <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.8 }}>
                    <p style={{ margin: '0 0 12px' }}>
                      This is the record of your signature. <strong>The agreement itself is the
                      document you signed</strong> — use <em>Download PDF</em> above to open your
                      executed copy, which is the authoritative version of your terms.
                    </p>
                    <p style={{ margin: 0, color: '#6b7280' }}>
                      Questions about anything in it — what you are paid, when, or what you agreed
                      to do — go to the Doorly team, who will answer from your signed agreement
                      rather than from a summary.
                    </p>
                  </div>

                  <div style={{ marginTop: 24, paddingTop: 16, borderTop: '2px solid #111827' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', marginBottom: 12 }}>ELECTRONIC SIGNATURE</div>
                    <div style={{ display: 'flex', gap: 20 }}>
                      <div><div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Signed By</div><div style={{ fontSize: 14, fontFamily: 'cursive', color: '#111827', borderBottom: '1px solid #111827', paddingBottom: 3 }}>{viewingAgreement.signed_name || ''}</div></div>
                      <div><div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Date</div><div style={{ fontSize: 12, color: '#111827', borderBottom: '1px solid #111827', paddingBottom: 3 }}>{viewingAgreement.uploaded_at ? new Date(viewingAgreement.uploaded_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</div></div>
                    </div>
                    <div style={{ marginTop: 20, padding: 10, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 9, color: '#9ca3af', textAlign: 'center' }}>
                      Executed electronically via DoorlyPro. Go Doorly, LLC | NMLS #2764749
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
